/**
 * useAutoSaveDraft — debounced auto-save of captured items (ported from
 * Noteble — ADR-022).
 *
 * Every captured element (photos, notes, voice transcripts, the in-progress
 * note) is continuously persisted into the active draft; clicking Next
 * flushes immediately (see CaptureAddWorkflow), so nothing is lost on
 * refresh or navigation.
 *
 * The draft now lands in IndexedDB *and* in the server-side capture buffer
 * (ADR-030 §9), which makes this debounce load-bearing rather than merely
 * polite: a flush is an HTTP request, so the 3s window — plus the immediate
 * flush on tab-hide and unmount, which are the moments a buffer would
 * otherwise be lost — is what keeps a keystroke from becoming a round-trip.
 */

import { useEffect, useRef, useCallback } from 'react';
import type { CapturedPhoto, RecordingNote } from './AddContext';
import type { DraftItems } from './useDrafts';
import type { WorkflowContext, WorkflowEvent } from './workflow';

const AUTO_SAVE_DEBOUNCE_MS = 3_000;

/**
 * Special note ID used to persist the in-progress text note inside the
 * draft's notes array without mixing with user-created notes.
 */
export const PENDING_NOTE_ID = '__pending_note__';

export function buildDraftItems(
  ctx: WorkflowContext,
  photos: CapturedPhoto[],
  notes: RecordingNote[],
): DraftItems {
  const items: DraftItems = {
    photos: photos.map((p) => ({
      blob: p.blob,
      timestamp: p.timestamp,
      ...(p.description ? { description: p.description } : {}),
      ...(p.mediaId ? { mediaId: p.mediaId } : {}),
      ...(p.mermaid ? { mermaid: p.mermaid } : {}),
    })),
    notes: notes.map((n) => ({
      id: n.id,
      text: n.text,
      timestamp: n.timestamp,
      ...(n.sourceType ? { sourceType: n.sourceType } : {}),
      ...(n.sourceTitle ? { sourceTitle: n.sourceTitle } : {}),
    })),
    streamingTranscripts: ctx.capturedStreams
      .filter((s) => s.transcript)
      .map((s) => ({ transcript: s.transcript!, duration: s.duration })),
  };
  if (ctx.note.trim().length > 0) {
    items.notes.push({ id: PENDING_NOTE_ID, text: ctx.note, timestamp: 0 });
  }
  return items;
}

interface UseAutoSaveDraftParams {
  ctx: WorkflowContext;
  dispatch: React.Dispatch<WorkflowEvent>;
  photos: CapturedPhoto[];
  notes: RecordingNote[];
  autoSaveDraft: (
    draftId: string,
    items: DraftItems,
    inputMethod: string,
    languageCode: string,
    templateIds?: string[],
  ) => Promise<void>;
  silentDeleteDraft: (draftId: string) => Promise<void>;
}

export function useAutoSaveDraft(params: UseAutoSaveDraftParams) {
  const { ctx, dispatch, photos, notes, autoSaveDraft, silentDeleteDraft } = params;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isSavingRef = useRef(false);
  const mountedRef = useRef(true);
  const hasSavedOnceRef = useRef(false);

  // Snapshot refs for stable reads inside flush
  const photosRef = useRef(photos);
  photosRef.current = photos;
  const notesRef = useRef(notes);
  notesRef.current = notes;
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  const flush = useCallback(async () => {
    const s = ctxRef.current;
    if (!mountedRef.current || isSavingRef.current) return;
    if (s.isSubmitting || s.isRestoring) return;

    const currentPhotos = photosRef.current;
    const currentNotes = notesRef.current;

    const hasItems = currentPhotos.length > 0
      || currentNotes.length > 0
      || s.capturedStreams.length > 0
      || s.note.trim().length > 0;

    const draftId = s.activeDraftId;

    if (!hasItems) {
      if (draftId) {
        await silentDeleteDraft(draftId);
        dispatch({ type: 'SET_ACTIVE_DRAFT_ID', id: null });
      }
      return;
    }

    if (!draftId) {
      const newId = `draft_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      dispatch({ type: 'SET_ACTIVE_DRAFT_ID', id: newId });
      return; // next flush picks up the new ID
    }

    isSavingRef.current = true;
    try {
      const items = buildDraftItems(s, currentPhotos, currentNotes);
      await autoSaveDraft(
        draftId, items, s.inputMethod, navigator.language || 'en-GB',
        s.selectedTemplates.map((t) => t.id),
      );
    } catch {
      // Already logged by autoSaveDraft
    } finally {
      isSavingRef.current = false;
    }
  }, [autoSaveDraft, silentDeleteDraft, dispatch]);

  // Debounced effect — watches all item sources
  useEffect(() => {
    if (ctx.isSubmitting || ctx.isRestoring) return;

    const hasItems = photos.length > 0 || notes.length > 0
      || ctx.capturedStreams.length > 0
      || ctx.note.trim().length > 0;

    if (timerRef.current) clearTimeout(timerRef.current);
    const delay = (hasItems && !hasSavedOnceRef.current) ? 0 : AUTO_SAVE_DEBOUNCE_MS;

    timerRef.current = setTimeout(() => {
      if (hasItems) hasSavedOnceRef.current = true;
      void flush();
    }, delay);

    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [
    photos, notes, ctx.capturedStreams, ctx.note,
    ctx.isSubmitting, ctx.isRestoring, ctx.activeDraftId, flush,
  ]);

  // Flush immediately when the tab is hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        if (timerRef.current) clearTimeout(timerRef.current);
        void flush();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [flush]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      void flush();
      mountedRef.current = false;
    };
  }, [flush]);

  return { flush };
}
